import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import { CartItemCard } from '@components/ProductOffers/modals/ShoppingCartDrawer/CartItemCard/CartItemCard';
import {
  formatPrice,
  getRecurringPrice,
} from '@components/ProductOffers/modals/ShoppingCartDrawer/utils/cartItemCard.utils';
import type { CartItemDTO } from '@t/api.types';
import type { ParsedEquipment } from '@t/equipmentOffering.types';
import type { CategoryType, Plan } from '@t/productOffering.types';

interface BundleCartItemCardProps {
  bundleItem: CartItemDTO;
  plansByCategory: Record<CategoryType, Plan[]>;
  equipmentByPlanId: Map<string, ParsedEquipment[]>;
  onDelete: () => void;
  onViewMemberDetails: (plan: Plan) => void;
}

function findPlanForMember(memberId: string | undefined, plansByCategory: Record<CategoryType, Plan[]>): Plan | null {
  if (!memberId) return null;
  for (const plans of Object.values(plansByCategory)) {
    const match = plans.find((p) => p.planId === memberId);
    if (match) return match;
  }
  return null;
}

export const isBundleItem = (item: CartItemDTO): boolean =>
  (item.childItems ?? []).some((c) => c.isMandatory && c.type !== 'equipment');

export const BundleCartItemCard = ({
  bundleItem,
  plansByCategory,
  equipmentByPlanId,
  onDelete,
  onViewMemberDetails,
}: BundleCartItemCardProps) => {
  const theme = useTheme();
  const mandatoryMembers = useMemo(
    () => (bundleItem.childItems ?? []).filter((c) => c.isMandatory && c.type !== 'equipment'),
    [bundleItem.childItems]
  );

  const discountAmount = getRecurringPrice(bundleItem);

  return (
    <Box
      sx={{
        border: `1.5px solid ${theme.novus.headerBorder}`,
        borderRadius: '10px',
        overflow: 'hidden',
      }}
    >
      {/* Bundle header */}
      <Box
        sx={{
          backgroundColor: theme.novus.fileThumbnailBg,
          borderBottom: `1.5px solid ${theme.novus.headerBorder}`,
          px: 2.5,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="label.button">{bundleItem.productOffering?.name ?? 'Bundle'}</Typography>
        {discountAmount != null && discountAmount < 0 && (
          <Typography variant="label.button" sx={{ color: theme.novus.discountGreen }}>
            Bundle Savings: -{formatPrice(Math.abs(discountAmount))}/mo
          </Typography>
        )}
      </Box>

      {/* Member plan cards */}
      <Stack spacing={0} divider={<Divider sx={{ borderColor: theme.novus.headerBorder }} />}>
        {mandatoryMembers.map((member) => {
          const memberId = member.productOffering?.id;
          const plan = findPlanForMember(memberId, plansByCategory);
          const equipment = equipmentByPlanId.get(memberId ?? '') ?? [];

          return (
            <CartItemCard
              key={member.id ?? memberId}
              item={member}
              plan={plan}
              equipmentList={equipment}
              onViewDetails={() => plan && onViewMemberDetails(plan)}
            />
          );
        })}
      </Stack>

      {/* Bundle footer: discount line + delete */}
      <Box
        sx={{
          backgroundColor: theme.novus.fileThumbnailBg,
          borderTop: `1.5px solid ${theme.novus.headerBorder}`,
          px: 2.5,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {discountAmount != null && discountAmount < 0 ? (
          <Box>
            <Typography variant="body.sm" color="text.secondary">
              Bundle Discount
            </Typography>
            <Typography variant="label.button" sx={{ color: theme.novus.discountGreen }}>
              -{formatPrice(Math.abs(discountAmount))}/month
            </Typography>
          </Box>
        ) : (
          <Box />
        )}

        <Typography
          variant="link.action"
          onClick={onDelete}
          sx={{ cursor: 'pointer' }}
        >
          Delete Bundle
        </Typography>
      </Box>
    </Box>
  );
};
